import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, map, filter } from 'lodash';
import { format } from 'date-fns';
import './SignDocReportCard.scss';
import { I18nReactContext } from '../../../contexts/I18nContext';
import useSignDocTypes from '../../../Hooks/useSignDocTypes';
import { Button } from '../../Form/Button/Button';
import { useUserAdminOrgs } from '../../Hooks/UseUserAdminOrgs';
import { SignDocTypes } from '../../SignDoc/SignDocLib';

/**
 * Wide card component for displaying SignDoc report summary
 * Features a full-width layout with SignDoc image and name on the top,
 * and three data columns on the bottom (progress, anonymous status, action button).
 * 
 * @param {object} params - Component parameters
 * @param {object} params.signDoc - The SignDoc object
 * @param {string} params.signDoc.id - SignDoc ID
 * @param {string} params.signDoc.docName - SignDoc name/title
 * @param {string} params.signDoc.description - SignDoc description
 * @param {string} params.signDoc.type - SignDoc type
 * @param {object} params.questionnaireReportData - The questionnaire report data
 * @param {number} params.questionnaireReportData.progressPercentage - Overall completion percentage (0-100)
 * @param {boolean} params.questionnaireReportData.isAnonymous - Whether survey allows anonymous responses
 * @param {string} params.questionnaireReportData.imgSrc - SignDoc image URL
 * @param {number} params.questionnaireReportData.totalAssigned - Total users assigned to SignDoc
 * @param {string} params.questionnaireReportData.sessionStartDate - Session start date for courses
 * @param {Array<string>} params.organizationIds - Array of organization IDs to filter users by
 * @param {Array<object>} params.adminOrganizationOptions - Array of admin organization options
 * @returns {JSX.Element} Rendered SignDoc report card component
 */
export const SignDocReportCard = ({
    signDoc,
    questionnaireReportData,
    organizationIds,
    adminOrganizationOptions
}) => {
    const { i18n } = useContext(I18nReactContext);
    const { getMgmntImageIcon, getConfigProperty, getHoneycombText } = useSignDocTypes();
    const navigate = useNavigate();
    const [assignedOrganizations, setAssignedOrganizations] = useState([]);

    // Extract SignDoc data with safe defaults
    const signDocId = get(signDoc, 'id', '');
    const signDocName = get(signDoc, 'docName', '');
    const signDocDescription = get(signDoc, 'description', '');
    const signDocType = get(signDoc, 'type', '');
    const imgSrc = get(signDoc, 'imgSrc', '');
    const progressPercentage = get(questionnaireReportData, 'progressPercentage', 0);
    const isAnonymous = get(questionnaireReportData, 'isAnonymous', false);
    const sessionStartDate = get(questionnaireReportData, 'sessionStartDate', null);
    const totalAssigned = get(questionnaireReportData, 'totalAssigned', 0);
    const organizationSignDocs = get(signDoc, 'organizationSignDoc', []);

    useEffect(() => {
        if(adminOrganizationOptions && adminOrganizationOptions.length > 0){
            let organizationNames = map(filter(organizationSignDocs, org => !get(org, 'organization.isPersonal', false)), (org) => {
                const orgId = get(org, 'OrganizationId');
                const orgName = adminOrganizationOptions.find(option => option.value === orgId)?.text;
                return orgName;
            }).filter(Boolean);
            setAssignedOrganizations(organizationNames);
        }
    }, [adminOrganizationOptions, organizationSignDocs]);

   

    /**
     * Handles the click event for viewing full report
     * Navigates to appropriate detail page based on SignDoc type configuration
     * @returns {void}
     */
    const handleViewReport = () => {
        // Navigate to SignDoc results detail page with organizationIds format
        const searchQuery = organizationIds.length > 0 ? `organizationIds=${organizationIds.join(',')}` : 'organizationIds=all';

        // Get the reports base path from config (defaults to /survey-results for backward compatibility)
        const basePath = getConfigProperty({
            type: signDocType,
            property: 'reportsBasePath',
            defaultValue: '/survey-results'
        });

        navigate({
            pathname: `${basePath}/${signDocId}`,
            search: searchQuery
        });
    };

    /**
     * Gets the display text for anonymous status
     * @param {object} params - Function parameters  
     * @param {boolean} params.isAnonymous - Whether survey is anonymous
     * @returns {string} Display text for anonymous status
     */
    const getAnonymousStatusText = ({ isAnonymous }) => {
        return isAnonymous 
            ? String(i18n.translate('surveyReports.card.anonymous'))
            : String(i18n.translate('surveyReports.card.identified'));
    };

    return (
        <div className="SignDocReportCard">
              {/* Top section - SignDoc Image and Name */}
              <div className="signDocInfoSection">
                <div className="signDocImageWrapper">
                    <img 
                        src={imgSrc || getMgmntImageIcon({ type: signDocType })} 
                        alt={signDocName}
                        className="signDocImage"
                    />
                </div>
                <div className="signDocTextInfo">
                    <div className="signDocName" title={signDocName}>
                        {signDocName}
                    </div>
                    {signDocDescription && (
                        <div className="signDocDescription" title={signDocDescription}>
                            {signDocDescription}
                        </div>
                    )}
                    {/* SignDoc Type */}
                    <div className="signDocTypeText">
                        {getHoneycombText({ type: signDocType })}
                    </div>
                </div>
            </div>

            <div className="organizationChipsSection">
                <div className="organizationsWrapper">
                    {assignedOrganizations.length > 0 && (
                        <>
                            {assignedOrganizations.slice(0, 10).map((org, index) => (
                                <span key={index} className="organizationName">
                                    {org}
                                </span>
                            ))}
                            {assignedOrganizations.length > 10 && (
                                <span className="organizationName">...</span>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Bottom section - Three data columns */}
            <div className="signDocDataSection">
                {/* Column 1: Progress Information */}
                <div className="dataColumn progressColumn">
                    <div className="columnValue">
                        <div className="progressPercentage">
                            {Math.round(progressPercentage)}%
                        </div>
                        <div className="progressSubtext">
                            {i18n.translate('surveyReports.card.responseRate')}
                        </div>
                    </div>
                </div>

                {/* Column 2: Type-Specific Information */}
                <div className="dataColumn statusColumn">
                    <div className="columnValue">
                        {(signDocType === SignDocTypes.SURVEY || signDocType === SignDocTypes.EXAM) ? (
                            // Questionnaire: Show anonymous status
                            <div className="anonymousStatus">
                                {getAnonymousStatusText({ isAnonymous })}
                            </div>
                        ) : (signDocType === SignDocTypes.ONLINE_COURSE || signDocType === SignDocTypes.FRONTAL_COURSE) ? (
                            // Course: Show session date
                            sessionStartDate ? (
                                <div className="sessionInfo">
                                    <div className="sessionDate">
                                        {format(new Date(sessionStartDate), 'dd/MM/yyyy HH:mm')}
                                    </div>
                                    <div className="sessionLabel">
                                        {i18n.translate('courseReports.card.sessionDate')}
                                    </div>
                                </div>
                            ) : (
                                <div className="noSessionDate">
                                    {i18n.translate('courseReports.card.noSessionDate')}
                                </div>
                            )
                        ) : null}
                    </div>
                </div>

                {/* Column 3: Action Button */}
                <div className="dataColumn actionColumn">
                    <div className="columnValue">
                        <Button
                            color="primary"
                            onClick={handleViewReport}
                            text={String(i18n.translate('surveyReports.card.viewFullReport'))}
                        />
                    </div>
                </div>
            </div>

          
        </div>
    );
}; 